import paths from "routes/paths"
import { lazy, Suspense, useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"
import { compose, withHooks, withStores } from "enhancers"

import { Redirect, Route, Switch } from "components"
import Loader from "components/Loader"
import { gql } from "@apollo/client"
import { getRefreshToken, getToken, removeRefreshToken, removeToken, setRefreshToken, setToken } from "api"
import { PreviewBenefit } from "constants/enums/preview-benefit"
import userStore from "stores/userStore"
import { isEmpty } from "lodash"

/** Route-level code splitting: benefit/editor/pdf stay out of the initial download. */
const Page404 = lazy(() => import("pages/auth/Page404"))
const Page500 = lazy(() => import("pages/auth/Page500"))
const SignInPage = lazy(() => import("pages/auth/SignIn"))
const HomePage = lazy(() => import("pages/main/home").then((m) => ({ default: m.HomePage })))
const AccountPage = lazy(() => import("pages/main/account").then((m) => ({ default: m.AccountPage })))
const ProfilePage = lazy(() => import("pages/main/profile").then((m) => ({ default: m.ProfilePage })))
const AboutBelivePage = lazy(() => import("pages/main/about-belive").then((m) => ({ default: m.AboutBelivePage })))
const SettingPage = lazy(() => import("pages/main/setting").then((m) => ({ default: m.SettingPage })))
const BenefitPage = lazy(() => import("pages/main/benefit").then((m) => ({ default: m.BenefitPage })))
const BenefitDetailPage = lazy(() => import("pages/main/benefit/detail").then((m) => ({ default: m.BenefitDetailPage })))
const InsuranceSelectorPage = lazy(() =>
  import("pages/main/benefit/InsuranceSelector").then((m) => ({ default: m.InsuranceSelectorPage })),
)
const ViewBenefitPage = lazy(() => import("pages/main/benefit/View").then((m) => ({ default: m.ViewBenefitPage })))
const ViewDetailBenefitPage = lazy(() =>
  import("pages/main/benefit/ViewDetail").then((m) => ({ default: m.ViewDetailBenefitPage })),
)
const BenefitDetailPackagePage = lazy(() =>
  import("pages/main/benefit/detailPackage").then((m) => ({ default: m.BenefitDetailPackagePage })),
)
const PackageSelectorPage = lazy(() =>
  import("pages/main/benefit/PackageSelector").then((m) => ({ default: m.PackageSelectorPage })),
)
const PreviewBenefitPage = lazy(() =>
  import("pages/main/benefit/PreviewBenefit").then((m) => ({ default: m.PreviewBenefitPage })),
)
const PreviewBenefitDetailPage = lazy(() =>
  import("pages/main/benefit/PreviewBenefit/detail").then((m) => ({ default: m.PreviewBenefitDetailPage })),
)
const PreviewBenefitDetailPackagePage = lazy(() =>
  import("pages/main/benefit/PreviewBenefit/detailPackage").then((m) => ({
    default: m.PreviewBenefitDetailPackagePage,
  })),
)
const PreviewInsuranceSelectorPage = lazy(() =>
  import("pages/main/benefit/PreviewBenefit/InsuranceSelector").then((m) => ({
    default: m.PreviewInsuranceSelectorPage,
  })),
)
const PreviewPackageSelectorPage = lazy(() =>
  import("pages/main/benefit/PreviewBenefit/PackageSelector").then((m) => ({
    default: m.PreviewPackageSelectorPage,
  })),
)

const InitialPages = () => (
  <Switch>
    <Route path={paths.signInPath()} exact component={SignInPage} />
    <Redirect to={paths.signInPath()} />
  </Switch>
)

const MainPages = () => (
  <Switch>
    <Route path={paths.landingPath()} exact component={HomePage} />
    <Route path={paths.accountPath()} exact component={AccountPage} />
    <Route path={paths.profilePath()} exact component={ProfilePage} />
    <Route path={paths.aboutBelivePath()} exact component={AboutBelivePage} />
    <Route path={paths.settingPath()} exact component={SettingPage} />
    <Route path={paths.benefitManagementPath(":id")} exact component={BenefitPage} />
    <Route path={paths.benefitCustomManagementPath(":id")} exact component={BenefitDetailPage} />
    <Route path={paths.benefitCustomManagementEditPath(":id")} exact component={InsuranceSelectorPage} />
    <Route path={paths.benefitPackageManagementPath(":id")} exact component={BenefitDetailPackagePage} />
    <Route path={paths.benefitPackageManagementEditPath(":id")} exact component={PackageSelectorPage} />
    <Route path={paths.viewBenefitPath(":id")} exact component={ViewBenefitPage} />
    <Route path={paths.viewDetailBenefitPath(":id")} exact component={ViewDetailBenefitPage} />
    <Redirect to={paths.landingPath()} />
  </Switch>
)

const GuestPages = () => (
  <Switch>
    <Route path={paths.page404Path()} exact component={Page404} />
    <Route path={paths.page500Path()} exact component={Page500} />
  </Switch>
)

const PreviewPage = () => (
  <Switch>
    <Route path={paths.previewBenefitPath(":id")} exact component={PreviewBenefitPage} />
    <Route path={paths.previewBenefitCustomPath(":id")} exact component={PreviewBenefitDetailPage} />
    <Route path={paths.previewBenefitPackagePath(":id")} exact component={PreviewBenefitDetailPackagePage} />
    <Route path={paths.previewBenefitCustomEditPath(":id")} exact component={PreviewInsuranceSelectorPage} />
    <Route path={paths.previewBenefitPackageEditPath(":id")} exact component={PreviewPackageSelectorPage} />
    <Redirect to={paths.previewBenefitPath()} />
  </Switch>
)

interface RoutesProps {
  initialized: boolean
  isAuthorized: boolean
  isPreview: boolean
}

const Routes = (props: RoutesProps) => {
  const { initialized, isAuthorized, isPreview } = props

  return (
    <Suspense fallback={<Loader />}>
      {isPreview ? (
        <PreviewPage />
      ) : !initialized ? (
        <GuestPages />
      ) : !isAuthorized ? (
        <InitialPages />
      ) : (
        <MainPages />
      )}
    </Suspense>
  )
}

export const API = {
  GET_CURRENT_USER: gql`
    query GET_CURRENT_USER {
      getCurrentAppUser {
        id
        email
        employee {
          id
          firstNameTh
          lastNameTh
          firstNameEn
          lastNameEn
          birthDate
          departmentEn
          groupEn
        }
      }
    }
  `,
}

const enhancer = compose(
  withStores((stores: any) => ({
    currentUser: stores.userStore.currentUser,
  })),
  withHooks((props: any, hooks: any) => {
    const { useLazyQuery, useUrlParam, useState, useRef } = hooks
    const params = useUrlParam()
    /** Ref so onCompleted sees portal login even when state update has not re-rendered yet */
    const isLoginWithPortalRef = useRef(false)
    const [themeName, setThemeName] = useState(null)

    const [fetchCurrentUser, currentUser] = useLazyQuery(API.GET_CURRENT_USER, {
      onCompleted: (data: any) => {
        userStore.setCurrentUser(data.getCurrentAppUser)
        const themeNameFormStorage = localStorage.getItem("themeName")
        const groupEn = data.getCurrentAppUser?.employee?.groupEn
        const themeChanged = themeNameFormStorage !== groupEn

        if (themeChanged) {
          const { employee } = data.getCurrentAppUser || {}
          const correctTheme = employee?.groupEn || "default"

          localStorage.setItem("themeName", correctTheme)
          setThemeName(correctTheme)
        } else {
          const correctTheme = themeNameFormStorage || "default"
          setThemeName(correctTheme)
          localStorage.setItem("themeName", correctTheme)
        }

        // SPA navigation: avoids aborting in-flight lazy chunks (window.location reload)
        if (themeChanged || isLoginWithPortalRef.current) {
          paths.homePath().push()
          isLoginWithPortalRef.current = false
        }
      },
    })

    const { pathname } = useLocation()
    const currentPath = useMemo(() => {
      return pathname.split("/")[1]
    }, [pathname])

    const token = getToken()
    const refreshToken = getRefreshToken()

    const isPreview = currentPath === PreviewBenefit.preview

    useEffect(() => {
      if (isPreview) {
        return
      }

      const { accessToken: accessTokenFromParams, refreshToken: refreshTokenFromParams } = params

      if (accessTokenFromParams && refreshTokenFromParams) {
        removeToken()
        removeRefreshToken()
        setToken(accessTokenFromParams)
        setRefreshToken(refreshTokenFromParams)
        isLoginWithPortalRef.current = true
        fetchCurrentUser()
      } else if (token && refreshToken) {
        fetchCurrentUser()
      } else {
        setThemeName("default")
      }
    }, [fetchCurrentUser, isPreview, params, refreshToken, token])

    const hasThemeName = !isEmpty(themeName)
    const isLogin = !!token && !!refreshToken
    const user = isLogin ? currentUser?.data?.getCurrentAppUser || {} : {}
    const hasCurrentUser = !isEmpty(user)

    const initialized = hasThemeName
    const isAuthorized = isLogin && hasCurrentUser

    return {
      initialized,
      isAuthorized,
      isPreview,
    }
  }),
)

export default enhancer(Routes)
